import { useState, useEffect } from 'react';
import { useAdminStore } from '../store/adminStore';
import { Coupon } from '../types/admin';
import {
  Plus,
  Edit,
  Trash2,
  Ticket,
  X,
  Save,
  ToggleLeft,
  ToggleRight,
  Percent,
  DollarSign,
  Calendar,
  Users,
  BarChart3,
} from 'lucide-react';

export default function AdminCoupons() {
  const {
    coupons,
    addCoupon,
    updateCoupon,
    deleteCoupon,
    toggleCouponActive,
    fetchCouponsFromAPI,
  } = useAdminStore();

  useEffect(() => {
    fetchCouponsFromAPI();
  }, []);

  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive' | 'expired'>('all');

  const [formData, setFormData] = useState({
    code: '',
    description: '',
    type: 'percentage' as 'percentage' | 'fixed',
    value: 0,
    minOrderAmount: 0,
    maxDiscountAmount: '',
    usageLimit: '',
    startDate: '',
    endDate: '',
    isActive: true,
  });

  const filteredCoupons = coupons.filter((coupon) => {
    const matchesSearch =
      coupon.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coupon.description.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    if (filterStatus === 'active') return coupon.isActive && !isExpired(coupon.endDate);
    if (filterStatus === 'inactive') return !coupon.isActive;
    if (filterStatus === 'expired') return isExpired(coupon.endDate);
    return true;
  });

  const openAddForm = () => {
    setEditingCoupon(null);
    setFormData({
      code: '',
      description: '',
      type: 'percentage',
      value: 0,
      minOrderAmount: 0,
      maxDiscountAmount: '',
      usageLimit: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      isActive: true,
    });
    setShowForm(true);
  };

  const openEditForm = (coupon: Coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      description: coupon.description,
      type: coupon.type,
      value: coupon.value,
      minOrderAmount: coupon.minOrderAmount,
      maxDiscountAmount: coupon.maxDiscountAmount?.toString() || '',
      usageLimit: coupon.usageLimit?.toString() || '',
      startDate: coupon.startDate,
      endDate: coupon.endDate,
      isActive: coupon.isActive,
    });
    setShowForm(true);
  };

  const handleSave = () => {
    if (!formData.code.trim() || !formData.description.trim()) {
      alert('Kupon kodu ve açıklama zorunludur.');
      return;
    }
    if (formData.value <= 0) {
      alert('İndirim sıfırdan büyük olmalıdır.');
      return;
    }
    if (!formData.startDate || !formData.endDate) {
      alert('Başlangıç ve bitiş tarihleri zorunludur.');
      return;
    }
    if (new Date(formData.endDate) <= new Date(formData.startDate)) {
      alert('Bitiş tarihi başlangıç tarihinden sonra olmalıdır.');
      return;
    }

    const couponData = {
      code: formData.code.toUpperCase().trim(),
      description: formData.description.trim(),
      type: formData.type,
      value: formData.value,
      minOrderAmount: formData.minOrderAmount,
      maxDiscountAmount: formData.maxDiscountAmount ? Number(formData.maxDiscountAmount) : null,
      usageLimit: formData.usageLimit ? Number(formData.usageLimit) : null,
      startDate: formData.startDate,
      endDate: formData.endDate,
      isActive: formData.isActive,
    };

    if (editingCoupon) {
      updateCoupon(editingCoupon.id, couponData);
    } else {
      addCoupon(couponData);
    }
    setShowForm(false);
    setEditingCoupon(null);
  };

  const handleDelete = (id: string) => {
    deleteCoupon(id);
    setDeleteConfirm(null);
  };

  const isExpired = (endDate: string) => {
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return new Date(endDate + 'T23:59:59') < todayStart;
  };

  const getCouponStatus = (coupon: Coupon) => {
    if (isExpired(coupon.endDate)) return { label: 'Süresi Dolmuş', color: 'bg-gray-100 text-gray-600' };
    if (!coupon.isActive) return { label: 'Pasif', color: 'bg-red-100 text-red-600' };
    return { label: 'Aktif', color: 'bg-green-100 text-green-600' };
  };

  const stats = {
    total: coupons.length,
    active: coupons.filter((c) => c.isActive && !isExpired(c.endDate)).length,
    totalUsage: coupons.reduce((sum, c) => sum + c.usageCount, 0),
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kupon Yönetimi</h1>
          <p className="text-sm text-gray-500 mt-1">Kampanya ve indirim kuponlarını yönetin</p>
        </div>
        <button
          onClick={openAddForm}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#E31E24] text-white rounded-xl hover:bg-[#c41a1f] transition-colors font-semibold text-sm"
        >
          <Plus className="w-4 h-4" />
          Yeni Kupon
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <Ticket className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-xs text-gray-500">Toplam Kupon</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
              <ToggleRight className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
              <p className="text-xs text-gray-500">Aktif Kupon</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.totalUsage}</p>
              <p className="text-xs text-gray-500">Toplam Kullanım</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Kupon kodu veya açıklama ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm"
            />
          </div>
          <div className="flex gap-2">
            {(['all', 'active', 'inactive', 'expired'] as const).map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  filterStatus === status
                    ? 'bg-[#E31E24] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {status === 'all' ? 'Tümü' : status === 'active' ? 'Aktif' : status === 'inactive' ? 'Pasif' : 'Süresi Dolmuş'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Coupon Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {filteredCoupons.length === 0 ? (
          <div className="p-12 text-center">
            <Ticket className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Kupon bulunamadı</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Kupon</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Tür</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase">İndirim</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Koşullar</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Kullanım</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Tarih</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Durum</th>
                  <th className="text-right px-6 py-4 text-xs font-semibold text-gray-500 uppercase">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredCoupons.map((coupon) => {
                  const status = getCouponStatus(coupon);
                  return (
                    <tr key={coupon.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-bold text-gray-900 text-sm tracking-wider">{coupon.code}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{coupon.description}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600">
                          {coupon.type === 'percentage' ? (
                            <Percent className="w-3.5 h-3.5" />
                          ) : (
                            <DollarSign className="w-3.5 h-3.5" />
                          )}
                          {coupon.type === 'percentage' ? 'Yüzde' : 'Sabit'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-bold text-[#E31E24]">
                          {coupon.type === 'percentage' ? `%${coupon.value}` : `${coupon.value}₺`}
                        </span>
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell">
                        <div className="text-xs text-gray-500 space-y-0.5">
                          {coupon.minOrderAmount > 0 && (
                            <p>Min. tutar: {coupon.minOrderAmount}₺</p>
                          )}
                          {coupon.maxDiscountAmount && (
                            <p>Maks. indirim: {coupon.maxDiscountAmount}₺</p>
                          )}
                          {coupon.usageLimit && (
                            <p>Limit: {coupon.usageLimit}</p>
                          )}
                          {coupon.minOrderAmount === 0 && !coupon.maxDiscountAmount && !coupon.usageLimit && (
                            <p className="text-gray-400">—</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 hidden lg:table-cell">
                        <div className="flex items-center gap-1.5 text-sm text-gray-600">
                          <Users className="w-3.5 h-3.5 text-gray-400" />
                          <span className="font-medium">{coupon.usageCount}</span>
                          {coupon.usageLimit && (
                            <span className="text-gray-400">/ {coupon.usageLimit}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 hidden lg:table-cell">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <Calendar className="w-3.5 h-3.5 text-gray-400" />
                          <span>{new Date(coupon.startDate).toLocaleDateString('tr-TR')} — {new Date(coupon.endDate).toLocaleDateString('tr-TR')}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2.5 py-1 rounded-lg text-xs font-semibold ${status.color}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => toggleCouponActive(coupon.id)}
                            className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
                            title={coupon.isActive ? 'Pasifleştir' : 'Aktifleştir'}
                          >
                            {coupon.isActive ? (
                              <ToggleRight className="w-4 h-4 text-green-500" />
                            ) : (
                              <ToggleLeft className="w-4 h-4 text-gray-400" />
                            )}
                          </button>
                          <button
                            onClick={() => openEditForm(coupon)}
                            className="p-2 rounded-lg hover:bg-blue-50 transition-colors text-gray-400 hover:text-blue-500"
                            title="Düzenle"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(coupon.id)}
                            className="p-2 rounded-lg hover:bg-red-50 transition-colors text-gray-400 hover:text-red-500"
                            title="Sil"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">
                {editingCoupon ? 'Kuponu Düzenle' : 'Yeni Kupon Oluştur'}
              </h2>
              <button
                onClick={() => { setShowForm(false); setEditingCoupon(null); }}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Code */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Kupon Kodu *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="Örn: TRX20"
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm font-mono tracking-wider"
                />
              </div>
              {/* Description */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Açıklama *</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Kupon açıklaması"
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm"
                />
              </div>
              {/* Type & Value */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">İndirim Türü</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as 'percentage' | 'fixed' })}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm"
                  >
                    <option value="percentage">% Yüzde</option>
                    <option value="fixed">₺ Sabit Tutar</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    İndirim Değeri {formData.type === 'percentage' ? '(%)' : '(₺)'} *
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.value || ''}
                    onChange={(e) => setFormData({ ...formData, value: Number(e.target.value) })}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm"
                  />
                </div>
              </div>
              {/* Min Order & Max Discount */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Min. Sipariş Tutarı (₺)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.minOrderAmount || ''}
                    onChange={(e) => setFormData({ ...formData, minOrderAmount: Number(e.target.value) })}
                    placeholder="0"
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Maks. İndirim Tutarı (₺)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.maxDiscountAmount}
                    onChange={(e) => setFormData({ ...formData, maxDiscountAmount: e.target.value })}
                    placeholder="Sınırsız"
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm"
                  />
                </div>
              </div>
              {/* Usage Limit */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Kullanım Limiti</label>
                <input
                  type="number"
                  min="0"
                  value={formData.usageLimit}
                  onChange={(e) => setFormData({ ...formData, usageLimit: e.target.value })}
                  placeholder="Sınırsız"
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm"
                />
              </div>
              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Başlangıç Tarihi *</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Bitiş Tarihi *</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#E31E24] focus:outline-none text-sm"
                  />
                </div>
              </div>
              {/* Active */}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Aktif</label>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                  className={`relative w-11 h-6 rounded-full transition-colors ${formData.isActive ? 'bg-green-500' : 'bg-gray-300'}`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${formData.isActive ? 'translate-x-5' : ''}`}
                  />
                </button>
              </div>
            </div>
            <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => { setShowForm(false); setEditingCoupon(null); }}
                className="flex-1 py-2.5 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors text-sm"
              >
                İptal
              </button>
              <button
                onClick={handleSave}
                className="flex-1 py-2.5 bg-[#E31E24] text-white font-semibold rounded-xl hover:bg-[#c41a1f] transition-colors text-sm flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                {editingCoupon ? 'Güncelle' : 'Oluştur'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full text-center">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-7 h-7 text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Kuponu Sil</h3>
            <p className="text-sm text-gray-500 mb-6">
              Bu kuponu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2.5 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors text-sm"
              >
                İptal
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 py-2.5 bg-red-500 text-white font-semibold rounded-xl hover:bg-red-600 transition-colors text-sm"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
